// Modelo de datos — Gestión Academia
// Refleja el Excel original (CONFIG, ALUMNOS, APODERADOS, CURSOS, PAGOS, GASTOS…) y lo extiende.

export type Practica = "volleyball" | "futbol" | "ambas";
export type Regimen = "normal" | "beca" | "exento";
export type EstadoGenerico = "activo" | "inactivo";

/** Estados de una cuota, equivalentes a la "Leyenda de Estados" del Excel. */
export type EstadoCuota =
  | "pagado"
  | "pendiente" // vencida sin pagar
  | "por_vencer" // aún dentro del desfase / antes del vencimiento
  | "exento"
  | "beca"
  | "abono"; // pago parcial / anticipado

export type MetodoPago =
  | "transferencia"
  | "efectivo"
  | "tarjeta"
  | "otro";

export type Rol = "admin" | "apoderado";

/** Configuración por práctica deportiva. */
export interface ConfigPractica {
  clave: Practica; // "volleyball" | "futbol"
  nombre: string; // "Vóleibol"
  valorCuota: number; // CLP
  diaVencimiento: number; // día del mes (post-desfase)
  cuenta: string; // "Banco Estado · Cta Cte 123456789"
}

/** Documento único: config/general */
export interface ConfigGeneral {
  nombreAcademia: string;
  anoLectivo: number;
  mesesActivos: number[]; // 3..11 (Marzo..Noviembre) por defecto
  mesesDesfase: number; // 1 = la cuota de marzo se paga en abril
  interesMoraPct: number; // % por mora
  diasGracia: number;
  descuentoHermanosPct: number;
  emailAdmin: string;
  simboloMoneda: string; // "$"
  moneda: string; // "CLP"
  logoUrl?: string;
  practicas: ConfigPractica[];
  actualizadoEn?: string;
}

/** /usuarios/{uid} — perfil del administrador (auth en Firebase Auth). */
export interface Usuario {
  id: string; // = uid de Firebase Auth
  email: string;
  nombre: string;
  rol: Rol;
  estado: EstadoGenerico;
  ultimoLogin?: string;
  creadoEn: string;
}

/** /apoderados/{id} */
export interface Apoderado {
  id: string;
  nombre: string;
  rut: string;
  email: string;
  telefono?: string;
  estado: EstadoGenerico;
  creadoEn: string;
}

/** /alumnos/{id} */
export interface Alumno {
  id: string;
  nombre: string;
  rut: string; // NUEVO respecto del Excel: se usa para el login del apoderado
  apoderadoId: string;
  apoderadoNombre?: string; // denormalizado
  cursoId?: string;
  cursoNombre?: string; // denormalizado
  practicas: Practica; // "volleyball" | "futbol" | "ambas"
  regimen: Regimen;
  valorEspecial?: number | null; // sobreescribe el valor de cuota si aplica
  fechaNacimiento?: string | null;
  fechaIngreso: string;
  estado: EstadoGenerico;
  observacion?: string;
  creadoEn: string;
}

/** /cursos/{id} */
export interface Curso {
  id: string;
  nombre: string;
  nivel: string;
  profesor?: string;
  cupos: number;
  cuposOcupados: number;
}

/** /cuotas/{id} — obligación mensual (unifica hojas mensuales + PAGOS del Excel). */
export interface Cuota {
  id: string;
  alumnoId: string;
  alumnoNombre: string; // denormalizado
  apoderadoId: string; // denormalizado (para reglas + portal)
  practica: Exclude<Practica, "ambas">; // una cuota es por práctica
  mes: number; // 1..12 (mes de la cuota, no de vencimiento)
  ano: number;
  montoBase: number;
  descuento: number; // CLP (hermanos, beca…)
  recargo: number; // CLP (mora)
  montoFinal: number; // base - descuento + recargo
  cuenta: string;
  fechaVencimiento: string; // ISO date — mes + desfase, día de vencimiento
  fechaPago?: string | null;
  estado: EstadoCuota;
  metodoPago?: MetodoPago | null;
  comprobante?: string | null; // N° recibo / referencia
  observaciones?: string;
  registradoPor?: string;
  actualizadoEn: string;
}

/** /gastos/{id} — caja chica. */
export interface Gasto {
  id: string;
  fecha: string; // ISO date
  ano: number;
  descripcion: string;
  categoria: string;
  monto: number;
  responsable?: string;
  comprobante?: string;
  creadoEn: string;
}

/** /logs/{id} — auditoría. */
export interface LogEntry {
  id: string;
  timestamp: string;
  usuario: string;
  rol: Rol;
  accion: string;
  entidad: string;
  entidadId?: string;
  detalles?: string;
  ip?: string;
}

export const MESES_ES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
] as const;

export const ESTADO_CUOTA_LABEL: Record<EstadoCuota, string> = {
  pagado: "Pagado",
  pendiente: "Pendiente",
  por_vencer: "Por vencer",
  exento: "Exento",
  beca: "Beca",
  abono: "Abono",
};
